/**
 * AutomationCrudPermissionScopePage — roles × scope permission grid.
 *
 * UI:
 *   - Left column: role rows (name + inherited-role hint).
 *   - Top header: resource × action columns (read/write/delete/execute per resource).
 *   - Cells: toggleable checkboxes; flipping from ON -> OFF on a destructive scope
 *     (delete/execute) prompts a confirm modal before committing.
 *   - "New Role" button opens a drawer (name + inherited role dropdown + starting scope).
 *
 * Legacy visual lineage: apps/dash-legacy/.../automation.tsx (offer rules page). The layout
 * pattern (header + primary CTA + card body + drawer for create/edit) is reused; the rules
 * DSL is replaced by a roles × scope matrix as required by the task.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useAutomationCrudPermissionScopeCreate,
	useAutomationCrudPermissionScopeDelete,
	useAutomationCrudPermissionScopeList,
	useAutomationCrudPermissionScopeUpdate,
} from "./hooks";
import type { PermissionAction, RoleScopeRow } from "./api";

// ─────────────────────────────────────────────────────────────────
// Tailwind class tokens (emerald/sky gradient, zinc borders, light)
// ─────────────────────────────────────────────────────────────────
const CARD = "rounded-lg border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20";
const BTN =
	"rounded-md border border-sky-600 bg-sky-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-md border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50";
const BTN_DANGER =
	"rounded-md border border-red-600 bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700";

// ─────────────────────────────────────────────────────────────────
// Grid columns (resource × action)
// ─────────────────────────────────────────────────────────────────
const RESOURCES: { id: string; label: string }[] = [
	{ id: "offers", label: "Offers" },
	{ id: "campaigns", label: "Campaigns" },
	{ id: "creatives", label: "Creatives" },
	{ id: "payouts", label: "Payouts" },
	{ id: "automation", label: "Automation" },
];
const ACTIONS: PermissionAction[] = ["read", "write", "delete", "execute"];
const DESTRUCTIVE: Set<PermissionAction> = new Set(["delete", "execute"]);

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────
function isChecked(
	scope: RoleScopeRow["scope_json"],
	resource: string,
	action: PermissionAction,
): boolean {
	const r = scope?.[resource];
	return Boolean(r && r[action]);
}

function toggleScope(
	scope: RoleScopeRow["scope_json"],
	resource: string,
	action: PermissionAction,
	next: boolean,
): RoleScopeRow["scope_json"] {
	const base = scope && typeof scope === "object" ? { ...scope } : {};
	const row = { ...(base[resource] ?? {}) };
	row[action] = next;
	base[resource] = row;
	return base;
}

// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────
export function AutomationCrudPermissionScopePage() {
	const listQuery = useAutomationCrudPermissionScopeList();
	const createMut = useAutomationCrudPermissionScopeCreate();
	const updateMut = useAutomationCrudPermissionScopeUpdate();
	const deleteMut = useAutomationCrudPermissionScopeDelete();

	const [drawerOpen, setDrawerOpen] = useState(false);
	const [newName, setNewName] = useState("");
	const [newInherited, setNewInherited] = useState("");
	const [newStartingScope, setNewStartingScope] =
		useState<RoleScopeRow["scope_json"]>({});
	const [confirm, setConfirm] = useState<{
		id: string;
		resource: string;
		action: PermissionAction;
		currentScope: RoleScopeRow["scope_json"];
	} | null>(null);

	const items = useMemo(() => listQuery.data?.items ?? [], [listQuery.data]);

	// ── Toggle handler (may trigger confirm modal for destructive removes) ──
	function handleToggle(row: RoleScopeRow, resource: string, action: PermissionAction) {
		const currently = isChecked(row.scope_json, resource, action);
		const next = !currently;
		// Confirm when REMOVING a destructive scope that's currently ON.
		if (currently && DESTRUCTIVE.has(action)) {
			setConfirm({ id: row.id, resource, action, currentScope: row.scope_json });
			return;
		}
		const nextScope = toggleScope(row.scope_json, resource, action, next);
		updateMut.mutate({ id: row.id, body: { scope_json: nextScope } });
	}

	function confirmRemove() {
		if (!confirm) return;
		const nextScope = toggleScope(confirm.currentScope, confirm.resource, confirm.action, false);
		updateMut.mutate(
			{ id: confirm.id, body: { scope_json: nextScope } },
			{ onSettled: () => setConfirm(null) },
		);
	}

	function openDrawer() {
		setNewName("");
		setNewInherited("");
		setNewStartingScope({});
		setDrawerOpen(true);
	}

	function closeDrawer() {
		setDrawerOpen(false);
	}

	function handleCreate(e: FormEvent) {
		e.preventDefault();
		if (!newName.trim()) return;
		createMut.mutate(
			{
				role_name: newName.trim(),
				inherited_role_id: newInherited || null,
				scope_json: newStartingScope,
			},
			{
				onSuccess: () => closeDrawer(),
			},
		);
	}

	return (
		<main
			data-testid="automation-crud-permission-scope-page"
			className="mx-auto flex w-full max-w-7xl flex-col gap-8 px-6 py-10"
		>
			{/* Header */}
			<header className="flex items-start justify-between gap-4">
				<div className="flex flex-col gap-2">
					<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
						automation-crud-permission-scope
					</p>
					<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
						CRUD Permission Scope
					</h1>
					<p className="text-sm text-zinc-500">
						Per-role matrix of resource × action grants. Toggle individual cells to grant
						or revoke; destructive removes (delete/execute) require confirmation.
					</p>
				</div>
				<button
					type="button"
					className={BTN}
					onClick={openDrawer}
					data-testid="automation-crud-permission-scope-open-drawer"
				>
					New Role
				</button>
			</header>

			{/* Scope grid */}
			<section className={CARD} aria-label="Role permission grid">
				{listQuery.isLoading ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="automation-crud-permission-scope-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p className="text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="automation-crud-permission-scope-empty"
					>
						No roles yet. Click "New Role" to create one.
					</p>
				) : (
					<div className="overflow-x-auto">
						<table
							className="min-w-full border-separate border-spacing-0 text-sm"
							data-testid="automation-crud-permission-scope-list"
						>
							<thead>
								<tr className="bg-zinc-50">
									<th
										scope="col"
										className="sticky left-0 z-10 border-b border-r border-zinc-200 bg-zinc-50 px-3 py-2 text-left font-semibold text-zinc-700"
									>
										Role
									</th>
									{RESOURCES.map((resource) => (
										<th
											key={resource.id}
											scope="col"
											colSpan={ACTIONS.length}
											className="border-b border-r border-zinc-200 px-3 py-2 text-center font-semibold text-zinc-700"
										>
											{resource.label}
										</th>
									))}
									<th
										scope="col"
										className="border-b border-zinc-200 px-3 py-2 text-center font-semibold text-zinc-700"
									>
										Actions
									</th>
								</tr>
								<tr className="bg-zinc-50">
									<th
										scope="col"
										className="sticky left-0 z-10 border-b border-r border-zinc-200 bg-zinc-50 px-3 py-1.5 text-left text-xs font-medium text-zinc-500"
									>
										&nbsp;
									</th>
									{RESOURCES.flatMap((resource) =>
										ACTIONS.map((action) => (
											<th
												key={`${resource.id}-${action}`}
												scope="col"
												className="border-b border-zinc-200 px-2 py-1.5 text-center text-xs font-medium text-zinc-500"
											>
												{action}
											</th>
										)),
									)}
									<th
										scope="col"
										className="border-b border-zinc-200 px-2 py-1.5 text-center text-xs font-medium text-zinc-500"
									>
										&nbsp;
									</th>
								</tr>
							</thead>
							<tbody>
								{items.map((row) => (
									<tr
										key={row.id}
										className="hover:bg-zinc-50"
										data-testid={`automation-crud-permission-scope-row-${row.id}`}
									>
										<th
											scope="row"
											className="sticky left-0 z-10 border-b border-r border-zinc-200 bg-white px-3 py-2 text-left align-top"
										>
											<div className="font-medium text-zinc-900">{row.role_name}</div>
											{row.inherited_role_id ? (
												<div className="text-xs text-zinc-500">
													inherits {row.inherited_role_id}
												</div>
											) : null}
										</th>
										{RESOURCES.flatMap((resource) =>
											ACTIONS.map((action) => {
												const checked = isChecked(row.scope_json, resource.id, action);
												const pending = updateMut.isPending;
												return (
													<td
														key={`${row.id}-${resource.id}-${action}`}
														className="border-b border-zinc-100 px-2 py-2 text-center"
													>
														<label className="inline-flex cursor-pointer items-center justify-center">
															<input
																type="checkbox"
																className="size-4 rounded border-zinc-300 text-sky-600 focus:ring-sky-500"
																checked={checked}
																disabled={pending}
																aria-label={`${row.role_name} ${resource.label} ${action}`}
																data-testid={`automation-crud-permission-scope-cell-${row.id}-${resource.id}-${action}`}
																onChange={() =>
																	handleToggle(row, resource.id, action)
																}
															/>
														</label>
													</td>
												);
											}),
										)}
										<td className="border-b border-zinc-100 px-2 py-2 text-center">
											<button
												type="button"
												className={BTN_GHOST}
												onClick={() => deleteMut.mutate(row.id)}
												disabled={deleteMut.isPending}
												data-testid={`automation-crud-permission-scope-delete-${row.id}`}
											>
												Delete
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
				{updateMut.error ? (
					<p className="mt-3 text-xs text-red-600" role="alert">
						{updateMut.error.message}
					</p>
				) : null}
			</section>

			{/* Confirm modal for destructive scope removal */}
			{confirm ? (
				<div
					role="dialog"
					aria-modal="true"
					aria-labelledby="confirm-title"
					className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 p-4"
					data-testid="automation-crud-permission-scope-confirm"
				>
					<div className="w-full max-w-md rounded-lg border border-zinc-200 bg-white p-5 shadow-lg">
						<h3 id="confirm-title" className="text-base font-semibold text-zinc-900">
							Remove destructive scope?
						</h3>
						<p className="mt-2 text-sm text-zinc-600">
							You are about to revoke <span className="font-mono">{confirm.action}</span>{" "}
							on <span className="font-mono">{confirm.resource}</span>. This may break
							running workflows that depend on this permission.
						</p>
						<div className="mt-4 flex items-center justify-end gap-2">
							<button
								type="button"
								className={BTN_GHOST}
								onClick={() => setConfirm(null)}
								data-testid="automation-crud-permission-scope-confirm-cancel"
							>
								Cancel
							</button>
							<button
								type="button"
								className={BTN_DANGER}
								onClick={confirmRemove}
								disabled={updateMut.isPending}
								data-testid="automation-crud-permission-scope-confirm-ok"
							>
								{updateMut.isPending ? "Removing." : "Remove scope"}
							</button>
						</div>
					</div>
				</div>
			) : null}

			{/* New role drawer */}
			{drawerOpen ? (
				<div
					role="dialog"
					aria-modal="true"
					aria-labelledby="drawer-title"
					className="fixed inset-0 z-40 flex justify-end bg-zinc-900/40"
					data-testid="automation-crud-permission-scope-drawer"
				>
					<form
						onSubmit={handleCreate}
						className="flex h-full w-full max-w-md flex-col gap-5 border-l border-zinc-200 bg-white p-6 shadow-xl"
						data-testid="automation-crud-permission-scope-create-form"
					>
						<div className="flex items-center justify-between">
							<h3 id="drawer-title" className="text-lg font-semibold text-zinc-900">
								New role
							</h3>
							<button
								type="button"
								className={BTN_GHOST}
								onClick={closeDrawer}
								data-testid="automation-crud-permission-scope-drawer-close"
							>
								Close
							</button>
						</div>

						<label className="flex flex-col gap-1">
							<span className="text-xs font-medium text-zinc-700">Role name</span>
							<input
								type="text"
								className={INPUT}
								placeholder="e.g. manager-readonly"
								value={newName}
								onChange={(e) => setNewName(e.currentTarget.value)}
								disabled={createMut.isPending}
								data-testid="automation-crud-permission-scope-name-input"
								required
							/>
						</label>

						<label className="flex flex-col gap-1">
							<span className="text-xs font-medium text-zinc-700">Inherited role</span>
							<select
								className={INPUT}
								value={newInherited}
								onChange={(e) => setNewInherited(e.currentTarget.value)}
								disabled={createMut.isPending}
								data-testid="automation-crud-permission-scope-inherited-select"
							>
								<option value="">— none —</option>
								{items.map((row) => (
									<option key={row.id} value={row.id}>
										{row.role_name}
									</option>
								))}
							</select>
						</label>

						<fieldset className="flex flex-col gap-2 rounded-md border border-zinc-200 p-3">
							<legend className="px-1 text-xs font-medium text-zinc-700">
								Starting scope
							</legend>
							<div className="max-h-64 overflow-y-auto">
								<table className="min-w-full text-xs">
									<thead>
										<tr>
											<th className="px-2 py-1 text-left font-medium text-zinc-500">
												Resource
											</th>
											{ACTIONS.map((action) => (
												<th
													key={action}
													className="px-2 py-1 text-center font-medium text-zinc-500"
												>
													{action}
												</th>
											))}
										</tr>
									</thead>
									<tbody>
										{RESOURCES.map((resource) => (
											<tr key={resource.id}>
												<td className="px-2 py-1 text-left text-zinc-700">
													{resource.label}
												</td>
												{ACTIONS.map((action) => {
													const checked = isChecked(
														newStartingScope,
														resource.id,
														action,
													);
													return (
														<td
															key={`${resource.id}-${action}`}
															className="px-2 py-1 text-center"
														>
															<input
																type="checkbox"
																className="size-4 rounded border-zinc-300 text-sky-600 focus:ring-sky-500"
																checked={checked}
																onChange={(e) =>
																	setNewStartingScope((prev) =>
																		toggleScope(
																			prev,
																			resource.id,
																			action,
																			e.currentTarget.checked,
																		),
																	)
																}
																aria-label={`new-role ${resource.label} ${action}`}
																data-testid={`automation-crud-permission-scope-new-cell-${resource.id}-${action}`}
															/>
														</td>
													);
												})}
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</fieldset>

						{createMut.error ? (
							<p className="text-xs text-red-600" role="alert">
								{createMut.error.message}
							</p>
						) : null}

						<div className="mt-auto flex items-center justify-end gap-2">
							<button
								type="button"
								className={BTN_GHOST}
								onClick={closeDrawer}
								disabled={createMut.isPending}
							>
								Cancel
							</button>
							<button
								type="submit"
								className={BTN}
								disabled={createMut.isPending || !newName.trim()}
								data-testid="automation-crud-permission-scope-submit"
							>
								{createMut.isPending ? "Saving." : "Create role"}
							</button>
						</div>
					</form>
				</div>
			) : null}
		</main>
	);
}

export default AutomationCrudPermissionScopePage;
